import type { ReactNode } from 'react';
import type { DripKpiCardModel } from '../types';
import { SITE_DRIP_SURFACE } from '../../../constants/siteUi';

function Icon({ children }: { children: ReactNode }) {
  return <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--home-miami-blue)]/15 text-[var(--home-miami-blue)] ring-1 ring-[var(--home-miami-blue)]/25">{children}</span>;
}

function IconGlyph({ k }: { k: DripKpiCardModel['icon'] }) {
  switch (k) {
    case 'campaigns':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M4 6h16M4 12h10M4 18h16" strokeLinecap="round" />
        </svg>
      );
    case 'active':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <circle cx={12} cy={12} r={3.25} />
          <path d="M12 5v2M12 17v2M5 12h2M17 12h2" strokeLinecap="round" />
        </svg>
      );
    case 'sent':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M4 7l8 5 8-5-8-5-8 5z" strokeLinejoin="round" />
          <path d="M4 12l8 5 8-5M4 17l8 5 8-5" strokeLinejoin="round" />
        </svg>
      );
    case 'open':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" strokeLinejoin="round" />
          <circle cx={12} cy={12} r={2.25} />
        </svg>
      );
    case 'click':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M14 4l6 6-9 10-6-2 1-6 8-8z" strokeLinejoin="round" />
        </svg>
      );
    case 'convert':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'revenue':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M12 3v18M17 7H9.5a3.5 3.5 0 100 7H14a3.5 3.5 0 110 7H7" strokeLinecap="round" />
        </svg>
      );
    case 'unsub':
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3" strokeLinecap="round" />
        </svg>
      );
  }
}

function deltaTone(kpi: DripKpiCardModel): string {
  const { deltaPct, trend, id } = kpi;
  const unsub = id === 'k8' || kpi.label.toLowerCase().includes('unsub');
  if (unsub) {
    if (deltaPct < 0) return 'text-emerald-300';
    if (deltaPct > 0) return 'text-rose-300';
    return 'text-[var(--home-muted-on-dark)]';
  }
  if (trend === 'down' && deltaPct < 0) return 'text-rose-300';
  if (trend === 'up' && deltaPct > 0) return 'text-emerald-300';
  if (trend === 'flat' || deltaPct === 0) return 'text-[var(--home-muted-on-dark)]';
  return deltaPct > 0 ? 'text-emerald-300' : 'text-rose-300';
}

export function DripKpiStrip({ kpis }: { kpis: DripKpiCardModel[] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
      {kpis.map((kpi) => (
        <article
          key={kpi.id}
          className={`flex min-h-[5.5rem] flex-col justify-between ${SITE_DRIP_SURFACE} p-3`}
          title={kpi.hint}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="font-display text-[0.6rem] font-semibold uppercase leading-tight tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              {kpi.label}
            </p>
            <Icon>
              <IconGlyph k={kpi.icon} />
            </Icon>
          </div>
          <div className="mt-2 flex items-end justify-between gap-2">
            <p className="font-body text-lg font-semibold tabular-nums tracking-tight text-[var(--home-text-on-dark)]">
              {kpi.value}
            </p>
            <p className={`font-mono text-[0.65rem] font-medium tabular-nums ${deltaTone(kpi)}`}>
              {kpi.deltaPct > 0 ? '+' : ''}
              {kpi.deltaPct.toFixed(1)}%
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}
